// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductAvailability from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import cartLineStyles from "@rugby/Overrides/Widgets/Cart/Cartline.style";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardCondensedStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineCardCondensedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productErpNumberText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    price?: ProductPriceStyles;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
}

export const cartLineCardCondensedStyles: CartLineCardCondensedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
    },
    orderLineInfoGridItem: {
        width: 10,
    },
    orderLineInfoContainer: {
        gap: 20,
    },
    infoLeftColumn: {
        width: [12, 12, 6, 6, 6],
    },
    infoLeftColumnContainer: {
        gap: 10,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    productErpNumberText: {
        css: css`
            width: 100%;
            word-wrap: break-word;
        `,
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productPriceAndQuantityContainer: {
        gap: 10,
    },
    priceGridItem: {
        width: 5,
    },
    price: {
        price: {
            priceText: {
                weight: "normal",
            },
        },
    },
    quantityGridItem: {
        width: 3,
    },
    extendedUnitNetPriceGridItem: {
        width: 4,
    },
    extendedUnitNetPrice: {
        text: {
            weight: 700,
        },
    },
    cartLineErrorMessageGridItem: {
        width: 12,
    },
    cartLineErrorMessageText: {
        color: "danger",
    },
};

const CartLineCardCondensed = ({
    cart,
    cartLine,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    extendedStyles,
    showPricing,
    isCheckoutReviewAndSubmitPage,
}: Props) => {
    const styles = cartLineStyles;

    return (
        <StyledWrapper
            {...styles.mainContainer}
            data-test-selector={`cartline_condensed_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.productInfoContainer}>
                    <StyledWrapper {...styles.productImageContainer}>
                        <ProductImage product={cartLine} extendedStyles={styles.productImageStyles} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productBrandAndDescriptionContainer}>
                        <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                        {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}

                        <StyledWrapper {...styles.flexColumn}>
                            <StyledWrapper {...styles.productProductNumberAndAvailabilityContainer}>
                                <ProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    extendedStyles={styles.productPartNumbersStyles}
                                />
                                {!cartLine.quoteRequired && (
                                    <StyledWrapper>
                                        <ProductAvailability
                                            productId={cartLine.productId!}
                                            availability={cartLine.availability!}
                                            unitOfMeasure={cartLine.unitOfMeasure}
                                            trackInventory={cartLine.trackInventory}
                                            extendedStyles={styles.productAvailability}
                                        />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                        {cartLine.isRestricted && (
                            <StyledWrapper {...styles.flexColumn}>
                                <Typography {...styles.cartLineErrorMessageText}>
                                    {translate("Restricted product")}
                                </Typography>
                            </StyledWrapper>
                        )}
                        {!cartLine.isActive && (
                            <StyledWrapper {...styles.flexColumn}>
                                <Typography {...styles.cartLineErrorMessageText}>
                                    {translate("Inactive Product")}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.productInfoRightContainer}>
                    <StyledWrapper {...styles.flexColumn}>
                        <ProductPrice
                            showLabel={true}
                            product={cartLine}
                            currencySymbol={cart.currencySymbol}
                            showInvalidPriceMessage={showInvalidPriceMessage}
                            extendedStyles={styles.productPrice}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.quantityAndRemoveContainer}>
                        <CartLineQuantity cart={cart} editable={editable} extendedStyles={styles.quantity} />
                    </StyledWrapper>

                    {cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                        <StyledWrapper {...styles.extendedUnitNetPriceContainer}>
                            <Typography variant="legend" {...styles.totalLabelText}>
                                {!enableVat
                                    ? translate("Subtotal")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                    ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                    : `${translate("Subtotal")} (${translate("Ex. VAT")})`}
                            </Typography>
                            <Typography
                                {...styles.extendedUnitNetPriceText}
                                data-test-selector="cartline_extendedUnitNetPrice"
                            >
                                {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                    : cartLine.pricing.extendedUnitNetPriceDisplay}
                            </Typography>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineCardCondensed));
